"use client";
import Image from "next/image";
import { useState, useEffect, useRef, useCallback, memo } from "react";
import Link from "next/link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { Product } from "@/lib/data";
import { categories } from "@/lib/data";
import { useLanguage } from "@/lib/i18n/language-context";
import { useRouter } from "next/navigation";
import { ProductImage } from "@/components/ui/optimized-image";
import clsx from "clsx";

interface ProductCardProps {
  product: Product;
}

interface LazyImageProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  onVisible?: () => void;
}

const LazyImage = ({ src, alt, width, height, onVisible }: LazyImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isError, setIsError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const observerCallback = useCallback<IntersectionObserverCallback>(
    ([entry]) => {
      if (entry.isIntersecting)  {
        setIsInView(true);
        onVisible?.();
      }
    },
    [onVisible]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, {
      rootMargin: "200px 0px",
      threshold: 0.01
    });

    const current = containerRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, [observerCallback]);

  return (
    <div ref={containerRef} className="aspect-square overflow-hidden relative bg-gray-100">
      {/* 错误状态显示 */}
      {isError && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50">
          <span className="text-sm text-red-600">加载失败</span>
        </div>
      )}

      {/* 渐进式加载层 */}
      {!isLoaded && !isError && (
        <Image
          src={src}
          alt={alt}
          width={width}
          height={height}
          className="absolute inset-0 w-full h-full blur-lg scale-150"
          quality={10}
          priority
          aria-hidden
        />
      )}

      {/* 主图片层 */}
      {isInView && !isError && (
        <Image
          src={src}
          alt={alt}
          width={width}
          height={height}
          className={clsx(
            "w-full h-full object-cover transition-opacity duration-300",
            isLoaded ? "opacity-100" : "opacity-0"
          )}
          quality={75}
          loading={isInView ? "eager" : "lazy"}
          onLoad={() => setIsLoaded(true)}
          onError={() => setIsError(true)}
        />
      )}
    </div>
  );
};

export function ProductCard({ product }: ProductCardProps) {
  const { t } = useLanguage();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAddToCartDialogOpen, setIsAddToCartDialogOpen] = useState(false);
  const formattedPrice = product.price;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [dialogImageIndex, setDialogImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [magnifierPosition, setMagnifierPosition] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const magnifierRef = useRef<HTMLDivElement>(null);
  const router = useRouter();


  // 处理触摸事件（移动端兼容）
  const handleTouchStart = () => setIsHovered(true);
  const handleTouchEnd = () => setIsHovered(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isDialogOpen && product.images  && product.images.length  > 1 && !isHovered) {
      interval = setInterval(() => {
        setDialogImageIndex((prevIndex) =>
          prevIndex === product.images.length  - 1 ? 0 : prevIndex + 1
        );
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isDialogOpen, product.images,  isHovered]);

  // 添加/移除移动端触摸事件监听器
  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    container.addEventListener('touchstart',  handleTouchStart);
    container.addEventListener('touchend',  handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart',  handleTouchStart);
      container.removeEventListener('touchend',  handleTouchEnd);
    };
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (imageContainerRef.current  && magnifierRef.current)  {
      const rect = imageContainerRef.current.getBoundingClientRect();
      const x = e.clientX  - rect.left;
      const y = e.clientY  - rect.top;

      setMagnifierPosition({ x, y });
    }
  };

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const isButton = (e.target  as HTMLElement).closest('button');
    if (!isButton) {
      const slug = product.id;
	  router.push(`/products/${slug}`);
    }
  };

  return (
    <>
      <Card className="group overflow-hidden transition-all duration-300 hover:shadow-lg
                         w-full md:w-auto max-w-[85vw] md:max-w-none mx-auto"
		onClick={handleCardClick}
      >
        <div className="aspect-square overflow-hidden">
          {product.images  && Array.isArray(product.images)  && (
            <LazyImage
              src={product.images[0]}
              alt={`${product.name}  - Main Preview`}
              width={400}
              height={400}
            />
          )}
        </div>
        <CardHeader className="p-3 md:p-4">
          <CardTitle className="line-clamp-1 text-lg md:text-lg">{product.name}</CardTitle>
          <CardDescription className="text-sm md:text-base">{formattedPrice}</CardDescription>
        </CardHeader>
        <CardFooter className="p-3 md:p-4 pt-0">
          <div className="flex w-full gap-2 md:gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs md:text-sm"
              onClick={() => setIsDialogOpen(true)}
            >
              {t("common.quickView")}
            </Button>
            <Button
              size="sm"
              className="flex-1"
              onClick={() => setIsAddToCartDialogOpen(true)}
            >
              {t("common.addToCart")}
            </Button>
          </div>
        </CardFooter>
      </Card>

      <Dialog open={isAddToCartDialogOpen} onOpenChange={setIsAddToCartDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <div className="p-4 space-y-4">
            <p className="text-sm">Please contact us via WhatsApp to complete your order.</p>
            <Button
              className="w-full bg-green-500 hover:bg-green-600"
              onClick={() => {
                window.open(`https://wa.me/8618259092797?text=Hi, I'm interested in this product: ${encodeURIComponent(product.name)}`, "_blank");
              }}
            >
              WhatsApp Us
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="dialog-desc-id">
          <DialogHeader>
            <DialogTitle className="
              text-lg sm:text-xl
              line-clamp-2 sm:line-clamp-1
              break-words
              min-h-[2.5em]
              transition-[line-clamp]
              md:whitespace-nowrap
            ">
              {product.name}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div
              ref={imageContainerRef}
              className="aspect-square max-h-[60vh] overflow-hidden rounded-md relative"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onMouseMove={handleMouseMove}
            >
              {product.images  && Array.isArray(product.images)  && (
                <Image
                src={product.images[dialogImageIndex]}
                alt={`Yupoo ${product.categories[0]} image ${dialogImageIndex + 1}`}
                width={500}
                height={500}
                className="h-full w-full object-contain"
                priority={true}
                quality={85}
                fetchPriority="high"
               />
              )}
              {isHovered && (
                <div
                  ref={magnifierRef}
                  className="absolute w-48 h-48 bg-white border border-gray-300 rounded-full overflow-hidden pointer-events-none"
                  style={{
                    left: magnifierPosition.x - 24,
                    top: magnifierPosition.y - 24,
                    backgroundImage: `url(${product.images ? encodeURI(product.images[dialogImageIndex]) : ''})`,
                    backgroundSize: '300%',
                    // 修正 backgroundPosition 的计算
                    backgroundPosition: `${-(magnifierPosition.x * 2 - 24)}px ${-(magnifierPosition.y * 2 - 24)}px`,
                  }}
                ></div>
              )}
            </div>
            <div className="flex flex-col justify-between py-2">
              <div className="space-y-3">
                <p className="text-lg font-semibold sm:text-xl">{formattedPrice}</p>
                <p className="text-sm text-muted-foreground sm:text-base">{product.description}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground sm:text-sm">
                      {t("common.category")}:
                    </span>
                      {(product.categories  || []).map((categoryId) => {
                        const categoryName = categories.find(c  => c.id  === categoryId)?.name || categoryId;

                        return (
                          <Link
                            key={categoryId}
                            href={`/products?category=${categoryId}`}
                            className="text-xs font-medium hover:underline sm:text-sm px-2 py-1 bg-accent rounded-md"
                          >
                            {categoryName?.charAt(0).toUpperCase() + categoryName?.slice(1)}
                          </Link>
                        );
                      })}
                  </div>
              </div>
              <Button className="mt-4 w-full sm:mt-6" 
                onClick={() => setIsAddToCartDialogOpen(true)}
              >
	        {t("common.addToCart")}
	      </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
